import { useEffect, useState } from 'react';
import type { Calendar, Subscription } from '../../shared/contracts';
import { api, type ClientSession } from '../api';
import { CalendarChoices } from '../components/SubscriptionModal';
import { ErrorMessage, Loading } from '../components/Status';

export function SubscriptionPage({
  id,
  calendars,
  session,
}: {
  id: string;
  calendars: Calendar[];
  session: ClientSession | null;
}) {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [recoveryInput, setRecoveryInput] = useState('');
  const [recoveryCredential, setRecoveryCredential] = useState('');
  const [newFeedUrl, setNewFeedUrl] = useState('');
  const [recovering, setRecovering] = useState(false);
  useEffect(() => {
    api
      .subscription(id)
      .then(({ subscription: value }) => {
        setSubscription(value);
        setSelected(value.calendarIds);
      })
      .catch((cause) => setError(cause.message));
  }, [id]);
  const save = async () => {
    setStatus('Saving…');
    setError('');
    try {
      const result = await api.updateSubscription(id, selected, session?.csrfToken);
      setSubscription(result.subscription);
      if (result.feedUrl) setNewFeedUrl(result.feedUrl);
      setStatus('Saved');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not update the feed');
      setStatus('');
    }
  };
  const recover = async () => {
    setRecovering(true);
    try {
      const result = await api.recoverSubscription(id, recoveryInput.trim(), session?.csrfToken);
      setSubscription(result.subscription);
      setSelected(result.subscription.calendarIds);
      setRecoveryCredential(result.recoveryCredential);
      setRecoveryInput('');
      setError('');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Recovery failed');
    } finally {
      setRecovering(false);
    }
  };
  const rotateRecovery = async () => {
    setRecovering(true);
    try {
      setRecoveryCredential(
        (await api.rotateSubscriptionRecovery(id, session?.csrfToken)).recoveryCredential,
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not create a recovery code');
    } finally {
      setRecovering(false);
    }
  };
  const remove = async () => {
    if (!window.confirm('Revoke this feed? Calendar apps using its address will stop updating.'))
      return;
    try {
      await api.deleteSubscription(id, session?.csrfToken);
      setSubscription(null);
      setStatus('Feed revoked');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not revoke the feed');
    }
  };
  if (error && !subscription)
    return (
      <main id="main-content" className="narrow-page">
        <h1>Recover your feed settings</h1>
        <ErrorMessage message={error} />
        <p>
          Use this feed's private recovery code to manage it on this browser. Public feeds do not
          need an account.
        </p>
        <label className="field">
          <span>Recovery code</span>
          <input
            value={recoveryInput}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setRecoveryInput(event.target.value)}
          />
        </label>
        <button
          className="button"
          disabled={recovering || !recoveryInput.trim()}
          onClick={() => void recover()}
        >
          Recover management access
        </button>
        <p>
          If you do not have the code, return to the original browser or{' '}
          <a href="/">create a new feed</a>.
        </p>
      </main>
    );
  if (!subscription && !status)
    return (
      <main id="main-content" className="narrow-page">
        <Loading label="Loading your feed" />
      </main>
    );
  if (!subscription)
    return (
      <main id="main-content" className="narrow-page">
        <div className="empty-state">
          <span>✓</span>
          <h1>{status}</h1>
          <p>This subscription no longer provides calendar access.</p>
          <a className="button" href="/">
            Return to calendar
          </a>
        </div>
      </main>
    );
  return (
    <main id="main-content" className="narrow-page manage-page">
      <a className="back-link" href="/">
        ← Back to calendar
      </a>
      <span className="eyebrow">Personal calendar</span>
      <h1>Manage your feed</h1>
      <p>Changes appear automatically in every calendar app using this subscription.</p>
      {newFeedUrl && (
        <section className="settings-card">
          <h2>Replace your subscription address</h2>
          <p>
            You added private calendars, so the old address has been revoked. Replace it in your
            calendar app with this new private address.
          </p>
          <label className="field">
            <span>New subscription address</span>
            <input readOnly value={newFeedUrl} onFocus={(event) => event.currentTarget.select()} />
          </label>
        </section>
      )}
      {recoveryCredential && (
        <section className="settings-card">
          <h2>Save your new recovery code</h2>
          <p>
            The previous recovery code no longer works. Save this code with this page's address in
            your password manager.
          </p>
          <label className="field">
            <span>New private recovery code</span>
            <input
              readOnly
              value={recoveryCredential}
              onFocus={(event) => event.currentTarget.select()}
            />
          </label>
        </section>
      )}
      {error && (
        <p className="inline-error" role="alert">
          {error}
        </p>
      )}
      <CalendarChoices calendars={calendars} selected={selected} onChange={setSelected} />
      <div className="manage-actions">
        <button className="button" disabled={!selected.length} onClick={() => void save()}>
          Save selection
        </button>
        <span role="status">{status}</span>
        <button className="link-button danger" onClick={() => void remove()}>
          Revoke feed
        </button>
      </div>
      <p>
        <button className="link-button" disabled={recovering} onClick={() => void rotateRecovery()}>
          Create a new private recovery code
        </button>
      </p>
    </main>
  );
}
